import moment from "moment";
import round from "lodash/round";
import _ from "lodash";
import {
  getLiftsSetsVolume,
  getVelocityDataFromParam,
  getWeightedIntensity,
} from "./actuals";
import { CHART, NAME_MAP } from "./get-zone";
import { linearRegression } from "simple-statistics";
import convertToRiR from "./converters/convert-intensity";

const {
  PARAMETER_OPTIONS: { REPS, LOAD, RPEAMRAP },
  OUTCOME_STATUS: { MISSED },
  DATE_FORMATS: { DATE_PICKER_FORMAT },
  RPE_STANDARD_TABLE,
  PARAMETER_OPTIONS,
  VELOCITY_LIFTING_STANDARDS,
} = require("../constants");

/**
 * Returns an array of exercises sorted ascending or descending
 *
 * @param exercises {Array} Array of exercises to sort
 * @param order {String} asc | desc
 * @returns {Exercise[]} sorted array of exercises
 */
const sortExercises = (exercises, order = "asc") => {
  if (!["asc", "desc"].includes(order)) {
    throw new Error("Invalid order. It must be either ascending or descending");
  }

  return exercises.slice(0).sort((a, b) => {
    const aDate = Number(a.utcDate);
    const bDate = Number(b.utcDate);

    return order === "asc" ? aDate - bDate : bDate - aDate;
  });
};

/**
 * Returns the index of the lowest and highest values of an array
 *
 * @param arr {Array} chart data array of shape { x: Date, y: Number }
 * @returns {Number[]} Array with minIndex on position 0, maxIndex on position 1
 */
const findIndexOfMinMax = (arr) => {
  if (!arr.length) {
    return [0, 0];
  }

  let minIndex = 0;
  let maxIndex = 0;
  let min = arr[0].y || 0;
  let max = arr[0].y || 0;

  for (let i = 0; i < arr.length; i++) {
    if (arr[i].y < min) {
      min = arr[i].y;
      minIndex = i;
    }
    if (arr[i].y > max) {
      max = arr[i].y;
      maxIndex = i;
    }
  }
  return [minIndex, maxIndex];
};

/**
 * Returns the desired value from an exercise based on a given param
 *
 * @param exercise {Object} exercise to get the value from
 * @param param {String} load | e1rm | volume | lifts | sets | missedSets | peakIntensity | avgIntensity
 * @param fromMetric {Function} locals.fromMetric function
 * @param units {String} metric | imperial
 * @param isRiR {Boolean}
 * @returns {Number} value from the exercise depending on passed param
 */
const getValue = (exercise, param, fromMetric, units, isRiR) => {
  const { primary, repMaximums, sets, secondaryRepMaximum, type } = exercise;
  const repMax =
    type === "secondary" && secondaryRepMaximum
      ? secondaryRepMaximum
      : repMaximums[primary] || 0;

  if (["load", "e1rm", "peakIntensity", "v1rm"].includes(param)) {
    const value = sets.reduce((max, set) => {
      if (!set.outcomes) return max;

      const maxOfSet = Object.values(set.outcomes).reduce((m, value) => {
        if (!value.outcome || value.status === MISSED) return m;
        const outcomeVal =
          param === "peakIntensity"
            ? value.outcome.load / repMax
            : value.outcome[param];
        return !!outcomeVal && Number(outcomeVal) > m ? Number(outcomeVal) : m;
      }, 0);

      return maxOfSet > max ? maxOfSet : max;
    }, 0);

    if (!value) return 0;

    return param === "peakIntensity"
      ? round(value * 100, 1)
      : round(fromMetric(value, units, false), 1);
  } else if (["lifts", "sets", "missedSets"].includes(param)) {
    return getLiftsSetsVolume(exercise)[param];
  } else if (["volume"].includes(param)) {
    //round and get value if its lb or kg for weekly and daily volume
    return round(
      fromMetric(getLiftsSetsVolume(exercise)[param], units, false),
      1
    );
  } else if (["avgIntensity"].includes(param)) {
    const { volume } = getLiftsSetsVolume(exercise);
    return round(getWeightedIntensity(exercise, volume) * 100, 1);
  } else if (["loadVelocity", "rpeVelocity"].includes(param)) {
    const parameter = ["loadVelocity"].includes(param) ? "load" : "rpe";

    const values = getVelocityDataFromParam(
      sets,
      parameter,
      fromMetric,
      units,
      isRiR
    );

    if (values) {
      return values;
    }

    return 0;
  }

  return 0;
};

const mapParam = (param, period) => {
  if (period === "week") return param.replace("weekly", "");
  return param;
};

/**
 * Returns an array of objects of shape { x: Date, y: Number } to use for chartist
 *
 * @param exercises {Array} Array of exercises
 * @param fromMetric {Function} locals.fromMetric function
 * @param param {String} load | e1rm | volume | lifts | sets | missedSets | peakIntensity | avgIntensity
 * @param units {String} metric | imperial
 * @param period {String} day | week
 * @param from {String} query.from
 * @param to {String} query.to
 * @returns {Object} object with data, minIdx, maxIdx
 */
export const getChartData = (
  exercises,
  fromMetric,
  param,
  units,
  period = "day",
  from,
  to,
  activeUser,
  athlete,
  isRiR
) => {
  const dates = {};
  let pointsForLinearRegression = [];
  const sortedExercises = sortExercises(exercises, "asc");
  const mappedParam = mapParam(param, period);
  const fromMoment = moment(from).startOf("day");

  sortedExercises.forEach((exercise) => {
    const value = getValue(exercise, mappedParam, fromMetric, units, isRiR);
    const date = moment(exercise.utcDate, "YYYYMMDD").format();

    if (period === "week" && value > 0) {
      const dateDiff = moment(date).diff(fromMoment, "week");
      if (!dates[dateDiff]) {
        dates[dateDiff] = [{ date, value }];
      } else if (dates[dateDiff] && ["volume"].includes(mappedParam)) {
        dates[dateDiff].push({ date, value });
      }
    } else if (value > 0 || (value === 0 && mappedParam === "missedSets")) {
      if (!dates[date]) {
        // value not logged, add it
        dates[date] = value;
      } else if (
        dates[date] &&
        value > dates[date] &&
        ["e1rm", "load", "peakIntensity"].includes(mappedParam)
      ) {
        // higher value found and looking for top value, replace previous value
        dates[date] = value;
      } else if (
        dates[date] &&
        ["volume", "lifts", "sets", "missedSets"].includes(mappedParam)
      ) {
        // value found and looking for cumulative data, add to previous value
        dates[date] += value;
      } else if (dates[date] && ["avgIntensity"].includes(mappedParam)) {
        // value found and looking for average data
        dates[date] = (dates[date] + value) / 2;
      }
    } else if (["loadVelocity", "rpeVelocity"].includes(mappedParam)) {
      if (value > 0 || value.length > 0) {
        if (!dates[date]) {
          // value not logged, add it
          dates[date] = value;
          pointsForLinearRegression = [...pointsForLinearRegression, ...value];
        } else if (
          dates[date] &&
          ["loadVelocity", "rpeVelocity"].includes(mappedParam)
        ) {
          // value found and added new points for the chart
          dates[date] = [...dates[date], ...value];
          pointsForLinearRegression = [...pointsForLinearRegression, ...value];
        }
      }
    }
  });

  if (["loadVelocity", "rpeVelocity"].includes(mappedParam)) {
    // As these two charts require a linear regression, a conditional case was made for them.
    // There is never going to be a week period for this type of chart
    const exercise = exercises[0];
    const { type, primary, secondary } = exercise;

    const velocityThresholds = activeUser.velocityThresholds || null;
    const threshold = velocityThresholds
      ? velocityThresholds[primary]
      : VELOCITY_LIFTING_STANDARDS[primary];

    let paramValue = 0;

    if (["loadVelocity"].includes(mappedParam)) {
      const repMax = getRMValue(athlete, type, primary, secondary);
      paramValue = fromMetric(repMax, units, false);
    } else {
      paramValue = convertToRiR(10, !isRiR);
    }

    const velocityThresholdPoint = [paramValue, threshold];

    const linearResult = linearRegression([
      ...pointsForLinearRegression,
      velocityThresholdPoint,
    ]);

    const data = Object.entries(dates).flatMap(([key, values]) => {
      return values.map(([param, velocity]) => {
        return {
          x: new Date(key),
          y: velocity,
          param,
          meta: moment(key).format("MMM D, YYYY"),
        };
      });
    });

    if (["loadVelocity"].includes(mappedParam) && data.length > 0) {
      // Push the resulting line from the formula to the data array
      data.push({ param: 0, line: round(linearResult.b, 3) });
      data.push({
        param: -linearResult.b / linearResult.m,
        line: 0,
      });

      // MVT Dash line data
      data.push({ mvt: threshold, param: 0 });
      data.push({ mvt: threshold, param: -linearResult.b / linearResult.m });
    } else if (["rpeVelocity"].includes(mappedParam) && data.length > 0) {
      const { b, m } = linearResult;

      const linePoint1 = m * convertToRiR(5, !isRiR) + b;
      const linePoint2 = m * convertToRiR(10, !isRiR) + b;

      // Push the resulting line from the formula to the data array
      data.push({ param: convertToRiR(5, !isRiR), line: round(linePoint1, 3) });
      data.push({
        param: convertToRiR(10, !isRiR),
        line: round(linePoint2, 3),
      });
    }

    // Should be changed to v1RM in future
    const [minIdx, maxIdx] = findIndexOfMinMax(data);

    return { data, minIdx, maxIdx };
  } else {
    const data =
      period === "week"
        ? Object.entries(dates).map(([key, values]) => {
            const value = values.reduce((prev, cur) => prev + cur.value, 0);
            const dateOne = fromMoment.clone().add(key, "week");
            const dateTwo = dateOne.clone().add(1, "week").subtract(1, "day");
            const start = moment.max(fromMoment, dateOne);
            const end = to
              ? moment.min(moment(to).startOf("day"), dateTwo)
              : dateTwo;
            return {
              x: new Date(start),
              y: value,
              meta: `${start.format("MMM D")} - ${end.format("D, YYYY")}`,
            };
          })
        : Object.entries(dates).map(([key, value]) => {
            return {
              x: new Date(key),
              y: value,
              meta: moment(key).format("MMM D, YYYY"),
            };
          });

    const [minIdx, maxIdx] = findIndexOfMinMax(data);

    return { data, minIdx, maxIdx };
  }
};

/**
 * Returns an array of filtered exercises
 *
 * @param exercises {Array} Array of exercises
 * @param filters {Object} filters object
 * @param primary {String} exercise primary
 * @param units {String} metric | imperial
 * @returns {Object[]} array with data to use for chartist
 */
export const filterExercises = (exercises, filters, primary, units) => {
  if (!exercises.length) return [];

  const { parameters } = exercises[0];

  // don't filter assistant without rpe reps load
  if (
    primary === "assistant" &&
    Array.isArray(parameters) &&
    (!parameters.includes(RPEAMRAP) ||
      !parameters.includes(REPS) ||
      !parameters.includes(LOAD))
  ) {
    return exercises;
  }

  const validFilters = Object.keys(filters).reduce((valid, key) => {
    if (![undefined, null, "", "All"].includes(filters[key])) {
      valid[key] = filters[key];
    }

    return valid;
  }, {});

  const filterConditions = [];

  if (!validFilters || Object.keys(validFilters).length === 0) {
    return Array.from(exercises);
  }

  if (validFilters.repsFrom) {
    const hasRepsFrom = (outcome) =>
      Number(outcome.reps) >= Number(validFilters.repsFrom);
    filterConditions.push(hasRepsFrom);
  }

  if (validFilters.repsTo) {
    const hasRepsTo = (outcome) =>
      Number(outcome.reps) <= Number(validFilters.repsTo);
    filterConditions.push(hasRepsTo);
  }

  if (validFilters.rpeFrom) {
    const hasRpeFrom = (outcome) =>
      Number(outcome.rpe) >= Number(validFilters.rpeFrom);
    filterConditions.push(hasRpeFrom);
  }

  if (validFilters.rpeTo) {
    const hasRpeTo = (outcome) =>
      Number(outcome.rpe) <= Number(validFilters.rpeTo);
    filterConditions.push(hasRpeTo);
  }

  if (validFilters.loadFrom) {
    const filterValue = Math.floor(
      Number(
        units === "imperial"
          ? validFilters.loadFrom / 2.204624
          : validFilters.loadFrom
      )
    );
    const hasLoadFrom = (outcome) => Number(outcome.load) >= filterValue;
    filterConditions.push(hasLoadFrom);
  }

  if (validFilters.loadTo) {
    const filterValue = Math.ceil(
      Number(
        units === "imperial"
          ? validFilters.loadTo / 2.204624
          : validFilters.loadTo
      )
    );
    const hasLoadTo = (outcome) => Number(outcome.load) <= filterValue;
    filterConditions.push(hasLoadTo);
  }

  let filteredExercises = exercises.slice(0);

  if (filterConditions.length > 0) {
    filteredExercises = filteredExercises.reduce((all, exercise) => {
      const sets = Array.from(exercise.sets).filter(
        (set) =>
          set.outcomes &&
          Object.keys(set.outcomes).reduce((include, setOrRoundIndex) => {
            return (
              include &&
              filterConditions.reduce(
                (include, fn) =>
                  include && fn(set.outcomes[setOrRoundIndex].outcome),
                true
              )
            );
          }, true)
      );

      if (sets.length) {
        all.push(exercise);
      }

      return all;
    }, []);
  }

  if (filters.sortOrder) {
    filteredExercises = sortExercises(filteredExercises, filters.sortOrder);
  }

  return filteredExercises;
};

/**
 * Returns an array of objects to use as options for a select input
 *
 * @param exerciseLibrary {Object} User's exercise library
 * @returns {Object[]} array of objects to use as options for react-select
 */
export const getMovementOptions = (exerciseLibrary) => {
  const movementOptions = [];
  Object.entries(exerciseLibrary).forEach(([primary, movements]) => {
    const formattedMovement =
      primary.charAt(0).toUpperCase() + primary.slice(1);
    if (!["assistant", "other"].includes(primary)) {
      movementOptions.push({
        label: formattedMovement,
        value: primary,
        primary,
        type: "primary",
        secondary: "",
      });
    }
    movements.forEach(({ movement, parameters, repMaximum }) => {
      const type = primary === "assistant" ? "assistant" : "secondary";
      const formatted = [
        "assistant",
        "other",
        "clean",
        "jerk",
        "snatch",
      ].includes(primary)
        ? ""
        : ` (${formattedMovement})`;
      movementOptions.push({
        label: movement + formatted,
        value: `${primary}${movement}`,
        primary,
        type,
        secondary: movement,
        parameters,
        repMaximum,
      });
    });
  });
  return movementOptions;
};

/**
 * Get the 1RM value to display
 *
 * @param user {Object} User to get RM value from
 * @param type {String} primary | secondary | assistant
 * @param primary {String} squat | bench | deadlift | assistant
 * @param secondary {String} secondary of the exercise
 * @returns {Number} RM Value
 */
export const getRMValue = (user, type, primary, secondary) => {
  const { exerciseLibrary, repMaximums } = user;

  if (type === "primary") {
    return (repMaximums && repMaximums[primary]) || 0;
  }

  if (type === "secondary") {
    const found = exerciseLibrary[primary].find(
      (item) => item.movement === secondary
    );

    if (found && found.customizedRM && found.repMaximum) {
      return found.repMaximum;
    }

    return (repMaximums && repMaximums[primary]) || 0;
  }

  return 0;
};

//----------------------------------------------------------- Init Rpe Report ---------------------------------------------------
const unitIncrements = {
  metric: 2.5,
  imperial: 5,
};

const roundIncrement = (val, units, roundIncrement) => {
  if (!val && val !== 0) return;

  if (roundIncrement) {
    const increment = unitIncrements[units];
    return Math.round(val / increment) * increment;
  }

  return Math.round(val * 100) / 100;
};

export const fromMetric = (val, units, round) => {
  if (!val) return;
  if (units === "imperial") val = val * 2.20462;
  return roundIncrement(val, units, round);
};

const fromIntensity = (intensity, reps) => {
  const percent = _.floor((intensity || 0) * 2) / 2;
  const bracket = CHART[percent];
  if (percent < 1) return "deload";
  if (percent > 100) return "max";
  return NAME_MAP[bracket[_.min([reps, bracket.length]) - 1]];
};

const getZone = (it, defaultRepMax) => {
  const repMax = it.equipped ? it.equippedRepMax : defaultRepMax;
  if (it.load > repMax) return NAME_MAP.M;
  if (it.load <= 0) return NAME_MAP.D;
  return fromIntensity((it.load / repMax) * 100, it.reps);
};

const roundValue = (value, unit, decimals = 1) => {
  return _.round(fromMetric(value, unit.units, unit.round), decimals);
};

/**
 * @typedef {object} TableParam
 * @property {object} rpeTable - from getRpeTable function
 * @property {number} rep - rep value
 * @property {number} rpe - rpe value
 * @property {object} unit - from useUnits hook
 * @property {object} bestSet - set with highest e1rm
 * @property {string} type - exercise type
 * @property {string} primary - exercise primary
 */

/**
 * Returns the value and zone to use on the RPE Table for a given rpe and rep combination
 *
 * @param tableParams {TableParam}
 * @returns {Object} value, zone and isActualValue for the table column
 */
export const getValueZone = ({
  rpeTable,
  rep,
  rpe,
  unit,
  bestSet,
  type,
  primary,
}) => {
  const set =
    rpeTable && rpeTable[rpe] && rpeTable[rpe][rep] ? rpeTable[rpe][rep] : {};
  const standard = RPE_STANDARD_TABLE[rpe][rep];
  const { intensity, repMax, utcDate } = set;
  const activeRepMax =
    unit.repMaximums[primary] !== 0
      ? unit.repMaximums[primary]
      : unit.coachRepMaximums[primary];
  const otherRepMax = bestSet.e1rm;
  const intensityVal = Math.min(intensity, 100);
  const defaultRepMax = repMax || activeRepMax;
  let zone = set.zone;
  let value = roundValue((intensityVal * defaultRepMax) / 100, unit);
  let isActualValue = true; // value is from a result, not plotted
  //if is kg or pounds
  if (!unit.is1RMPpercentage) {
    if (type === "assistant") {
      if (rpe === 10 && rep == 1) {
        // top e1rm is used as 100% (rpe10, 1rep) for "Other" exercises
        value = roundValue(otherRepMax, unit);
        zone = getZone({ reps: rep, load: otherRepMax }, otherRepMax);
      } else {
        // rest of the table is plotted for "Other" exercises using the best e1rm and RPE Standard Table
        const load = (otherRepMax * standard.intensity) / 100;
        value = roundValue(load, unit);
        zone = getZone({ reps: rep, load }, otherRepMax);
        isActualValue = false;
      }
    }
    if (type !== "assistant" && (!intensity || intensity <= 0)) {
      // plot value with current RM if there is no intensity found for this rpe and rep
      const repMaxToUse = activeRepMax ? activeRepMax : repMax;
      const load = (repMaxToUse * standard.intensity) / 100;
      value = roundValue(load, unit);
      zone = getZone({ reps: rep, load }, repMaxToUse);
      isActualValue = false;
    }
    //if it is %1rm
  } else {
    if (type === "assistant") {
      if (rpe === 10 && rep == 1) {
        // top e1rm is used as 100% (rpe10, 1rep) for "Other" exercises
        value = roundValue(otherRepMax, unit);
        zone = getZone({ reps: rep, load: otherRepMax }, otherRepMax);
      } else {
        // rest of the table is plotted for "Other" exercises using the best e1rm and RPE Standard Table
        const load = (otherRepMax * standard.intensity) / 100;
        value = roundValue(load, unit);
        zone = getZone({ reps: rep, load }, otherRepMax);
        isActualValue = false;
      }
    }
    if (type !== "assistant" && (!intensity || intensity <= 0)) {
      // plot value with current RM if there is no intensity found for this rpe and rep
      const repMaxToUse = activeRepMax ? activeRepMax : repMax;
      const load = (repMaxToUse * standard.intensity) / 100;
      const valueWithouth1RMPercentage = roundValue(load, unit);
      value = Math.round((100 * valueWithouth1RMPercentage) / activeRepMax);
      zone = getZone({ reps: rep, load }, repMaxToUse);
      isActualValue = false;
    }
  }
  return { value, zone, isActualValue, utcDate };
};

/**
 * Returns the set with the highest e1rm
 *
 * @param sets {Array}
 * @returns {Object} peak e1rm set
 */
export const getBestSet = (sets = []) => {
  return sets.reduce((prev, current) => {
    return current.e1rm > prev.e1rm ? current : prev;
  });
};

/**
 * Returns the rep maximum for a given exercise and set
 *
 * @param exercise {Object}
 * @param set {Object}
 * @returns {Number} rep maximum of the exercise
 */
export const getRepMax = (exercise, set) => {
  const { primary, secondaryRepMaximum, type } = exercise;
  let defaultRepMax =
    (exercise.repMaximums && exercise.repMaximums[primary]) || 0;

  if (!set.equipped && type == "secondary" && secondaryRepMaximum) {
    defaultRepMax = secondaryRepMaximum;
  }

  return set.equipped ? set.equippedRepMax : defaultRepMax;
};

/**
 * Returns the outcome values for the peak load of a set
 *
 * @param set {Object}
 * @returns {Object} object with load, e1rm, reps, volume and rpe
 */
export const getOutcomeValues = (set) => {
  const setRepsToUse = set && set.reps ? set.reps : 0;
  const setRpeToUse = set && set.rpe ? set.rpe : 0;

  let load = 0,
    e1rm = 0,
    reps = setRepsToUse,
    rpe = setRpeToUse;

  if (_.isEmpty(set.outcomes)) {
    return { load, e1rm, reps, volume: 0, rpe };
  }

  const completed = Object.values(set.outcomes).filter(
    ({ status }) => status === "completed"
  );

  if (!completed.length) {
    return { load, e1rm, reps, volume: 0, rpe };
  }

  const highestOutcome = completed.reduce((prev, current) => {
    const load = current.outcome ? current.outcome.load : 0;
    const prevLoad = prev.outcome && prev.outcome.load ? prev.outcome.load : 0;
    return load && Number(load) > prevLoad ? current : prev;
  });

  if (highestOutcome) {
    const { outcome } = highestOutcome;
    const loadToUse = outcome && outcome.load ? outcome.load : 0;
    const e1rmToUse = outcome && outcome.e1rm ? outcome.e1rm : 0;
    const repsToUse = outcome && outcome.reps ? outcome.reps : 0;
    const rpeToUse = outcome && outcome.rpe ? outcome.rpe : 0;

    load = Number(loadToUse);
    e1rm = Number(e1rmToUse);
    reps = Number(repsToUse);
    rpe = Number(rpeToUse);
  }

  const volume = completed.reduce((acc, current) => {
    let outcomeVolume = 0;
    if (current.outcome && current.outcome.load) {
      outcomeVolume =
        Number(current.outcome.reps) * Number(current.outcome.load);
    }
    return acc + outcomeVolume;
  }, 0);

  return { load, e1rm, reps, volume, rpe };
};

/**
 * Returns the set with the highest intensity for each rep and rpe combination
 *
 * @param sets {Array}
 * @param hasRepsLoadRpe {Boolean} exercise has reps, load and rpe in parameters
 * @param type {String} exercise type
 * @param bestSet {Object} set with highest e1rm
 * @returns {Object} object with rpe as keys, values are also objects representing reps
 */
export const getRpeTable = (sets = [], hasRepsLoadRpe, type, bestSet) => {
  const isOther = type === "assistant";
  // No RPE Table for exercises without reps, loads or rpe
  // "Other" exercises require the best set's e1rm to plot the table
  const e1RMToUse = bestSet.e1rm !== undefined ? bestSet.e1rm : undefined;
  if (!hasRepsLoadRpe || (isOther && !e1RMToUse)) return null;

  return (
    _(sets)
      // Ignore sets with less than 5 rpe.
      // Ignore sets with more than 12 reps.
      .filter((set) => {
        return set.rpe >= 5 && set.reps <= 12;
      })
      // Group sets by rpe.
      .groupBy("rpe")
      // Then we subgroup by reps.
      .mapValues((sets) => {
        return (
          _(sets)
            // Groups the sets by reps.
            .groupBy("reps")
            .mapValues((sets) => {
              // Find the set with the highest instensity
              return _.chain(sets)
                .map((set) => {
                  // for "Other" exercises, we use the best e1rm as the repMax
                  if (isOther) set.repMax = bestSet.e1rm;
                  const intensity = getIntensityAndZone(set);

                  return Object.assign({}, set, {
                    intensity: _.round(intensity * 100, 1),
                  });
                })
                .maxBy("intensity")
                .value();
            })
            .value()
        );
      })
      .omitBy(_.isEmpty)
      .value()
  );
};

/**
 * Get the intensity for a set. Will also update the set.zone according to the outcome reps and load
 * @param {Object} set - Set object
 * @returns {Number} intensity of a given set
 */
const getIntensityAndZone = (set) => {
  const { load, reps, repMax } = set;

  if (repMax <= 0) {
    return 0;
  }

  const it = { load: Number(load), reps };
  // Workouts that were created without target loads will always have "deload" zone
  // getting the zone with the outcome load so it updates properly
  set.zone = getZone(it, repMax);
  return Number(load) / repMax;
};

/**
 * Determine if the exercise should have e1RM value (true for powerlifting movements
 * and assistant type that has reps and load in parameters)
 *
 * @param exercise {Object} Exercise to evaluate
 * @returns {Boolean} true if exercise should have e1RM, false otherwise
 */
export const shouldHaveE1RM = (exercise) => {
  const { REPS, LOAD } = PARAMETER_OPTIONS;
  if (!exercise.parameters) {
    // Powerlifting movement (sets, reps, rpe, load) - should have e1RM
    return true;
  } else if (
    exercise.parameters &&
    exercise.parameters.includes(REPS) &&
    exercise.parameters.includes(LOAD)
  ) {
    // Movement with parameters including reps and load - should have e1RM
    return true;
  }

  return false;
};

/**
 * Determine if the exercise has RPE
 *
 * @param exercise {Object} Exercise to evaluate
 * @returns {Boolean} true if exercise has RPE, false otherwise
 */

export const hasRpe = (exercise) => {
  const { RPEAMRAP } = PARAMETER_OPTIONS;
  if (!exercise.parameters) {
    // Powerlifting movements have RPE
    return true;
  } else if (exercise.parameters && exercise.parameters.includes(RPEAMRAP)) {
    // Movement with RPE in parameters
    return true;
  }
  return false;
};

/**
 * Returns the set with the highest load for each rep range
 *
 * @param sets {Array}
 * @returns {Object} object with reps as keys, values are the sets with peak load
 */
export const getPersonalRecordLoad = (sets = []) => {
  return _(sets)
    .filter(setHasLoadAndReps)
    .groupBy("reps")
    .mapValues((sets) => {
      return _.chain(sets).maxBy("load").value();
    })
    .omitBy(_.isEmpty)
    .value();
};

/**
 * Determine if a set has load and reps (reps between 1 and 12)
 *
 * @param set {Object}
 * @returns {Boolean}
 */
const setHasLoadAndReps = (set) => {
  if (!set.reps || !set.load) return false;
  if (set.reps <= 30 && set.reps > 0 && set.load > 0) {
    return true;
  }
  return false;
};

//----------------------------------------------------------- End Rpe Report ---------------------------------------------------
